import { Box3, Vector2, Vector3 } from 'three';

import cameraManager from '../camera-manager';
import { SkyviewOrthographicCamera } from '../cameras/skyview-orthographic-camera';
import { CoordinateManager } from '../coordinate-manager';
import { InputHandler } from '../input-handler';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { Tool } from './tool';
import {
  getAngleAroundFrame,
  getReportFrameBounds,
  pickReportFrame,
  ReportFrame,
  ReportFramePart,
  ReportFrameState,
  snapFrameRotation,
} from './utils/report-frame';

type FrameDrag =
  | { part: 'center'; offset: Vector2 }
  | { part: 'edge'; startAngle: number; startRotation: number };

/** How close to the frame's center or edges the pointer picks them, in pixels. */
const PICK_TOLERANCE_PX = 8;

/** The margin around the frame when the view is fitted to it, as a part of its size. */
const FIT_MARGIN = 0.1;

const cursors: Record<ReportFramePart, { hover: string; drag: string }> = {
  center: { hover: 'move', drag: 'move' },
  edge: { hover: 'grab', drag: 'grabbing' },
};

export class ReportTool extends Tool {
  private readonly frameObject = new ReportFrame();
  private frame: ReportFrameState | undefined;
  private frameShown = false;
  private drag: FrameDrag | undefined;
  private hovered: ReportFramePart | undefined;
  private readonly frameListeners = new Set<() => void>();

  constructor(inputHandler: InputHandler) {
    super('report', inputHandler);
    this.visible = false;
    // TODO: Remove this once the follow-up ticket is implemented.
    // loadFonts();

    this.frameObject.visible = false;
    this.add(this.frameObject);
  }

  enable() {
    this.visible = true;
  }

  disable() {
    this.visible = false;
    this.showFrame(false);
  }

  /** The CAD drawing report's frame, kept while the tool is off so it's where the user left it. */
  getFrame(): ReportFrameState | undefined {
    return this.frame;
  }

  setFrame(frame: ReportFrameState) {
    this.frame = frame;
    this.placeFrame();
    this.frameListeners.forEach((listener) => listener());
  }

  /** Listens for changes to the frame, from setFrame or from the user moving or turning it. */
  subscribeToFrame(listener: () => void) {
    this.frameListeners.add(listener);
    return () => {
      this.frameListeners.delete(listener);
    };
  }

  /** Shows the frame in the viewer and lets the user move and turn it, or hides it. */
  showFrame(shown: boolean) {
    this.frameShown = shown;
    this.placeFrame();

    if (!shown) {
      this.drag = undefined;
      this.setHovered(undefined);
    }
  }

  /** Zooms the top-down view so the whole frame is in it, with a margin. */
  fitViewToFrame() {
    const camera = this.getTopDownCamera();
    if (!this.frame || !camera) {
      return;
    }

    const bounds = getReportFrameBounds(this.frame);
    const origo = this.getOrigo();
    camera.focusOnBox(
      new Box3(
        new Vector3(bounds.min.x - origo.x, bounds.min.y - origo.y, 0),
        new Vector3(bounds.max.x - origo.x, bounds.max.y - origo.y, 0),
      ),
      FIT_MARGIN,
    );
  }

  /**
   * The center and size of the top-down view, in the project's coordinate system. Undefined
   * outside the top-down view.
   */
  getView(): { center: Vector2; width: number; height: number } | undefined {
    const camera = this.getTopDownCamera();
    if (!camera) {
      return undefined;
    }

    const size = camera.getViewSize();
    const origo = this.getOrigo();
    return {
      center: new Vector2(camera.position.x + origo.x, camera.position.y + origo.y),
      width: size.x,
      height: size.y,
    };
  }

  onMouseDown(mouseInputData: MouseInputData) {
    super.onMouseDown(mouseInputData);

    const point = this.getPointerPoint(mouseInputData);
    if (!point || !this.frame || mouseInputData.button.type !== MouseButtonType.LeftButton) {
      return true;
    }

    const part = pickReportFrame(point, this.frame, this.getPickTolerance());
    if (!part) {
      return true;
    }

    this.drag =
      part === 'center'
        ? { part, offset: this.frame.center.clone().sub(point) }
        : {
            part,
            startAngle: getAngleAroundFrame(point, this.frame),
            startRotation: this.frame.rotation,
          };
    this.inputHandler.setCursor(cursors[part].drag);

    // The camera doesn't pan while the frame is dragged.
    return false;
  }

  onMouseMove(mouseInputData: MouseInputData) {
    const point = this.getPointerPoint(mouseInputData);
    if (!point || !this.frame) {
      return true;
    }

    if (this.drag) {
      this.dragFrame(this.frame, this.drag, point);
      return false;
    }

    this.setHovered(pickReportFrame(point, this.frame, this.getPickTolerance()));
    return true;
  }

  async onMouseUp(mouseInputData: MouseInputData) {
    if (this.drag) {
      this.endDrag();
      return false;
    }

    return super.onMouseUp(mouseInputData);
  }

  onMouseOut() {
    // The tool doesn't hear the mouse button being released outside the viewer.
    this.endDrag();
    return true;
  }

  onAnimate() {
    const camera = this.getTopDownCamera();
    if (!this.frameObject.visible || !camera) {
      return;
    }

    // Halfway between the camera's near and far planes.
    const height = new Vector3(0, 0, 0).unproject(camera).z;
    this.placeFrame();
    this.frameObject.onAnimate(
      cameraManager().getCanvasWidth(),
      cameraManager().getCanvasHeight(),
      height,
    );
  }

  private dragFrame(frame: ReportFrameState, drag: FrameDrag, point: Vector2) {
    if (drag.part === 'center') {
      this.setFrame({ ...frame, center: point.clone().add(drag.offset) });
    } else {
      const angle = getAngleAroundFrame(point, frame);
      this.setFrame({
        ...frame,
        rotation: snapFrameRotation(drag.startRotation + angle - drag.startAngle),
      });
    }
  }

  private endDrag() {
    if (!this.drag) {
      return;
    }

    this.drag = undefined;
    this.inputHandler.setCursor(this.hovered ? cursors[this.hovered].hover : '');
  }

  private setHovered(part: ReportFramePart | undefined) {
    if (part === this.hovered) {
      return;
    }

    this.hovered = part;
    this.frameObject.setHovered(part);
    this.inputHandler.setCursor(part ? cursors[part].hover : '');
  }

  private placeFrame() {
    this.frameObject.visible = this.frameShown && this.frame !== undefined;
    if (!this.frame) {
      return;
    }

    const origo = this.getOrigo();
    this.frameObject.update(
      this.frame,
      new Vector2(this.frame.center.x - origo.x, this.frame.center.y - origo.y),
    );
  }

  /** The point under the pointer, in the project's coordinate system, in the top-down view. */
  private getPointerPoint(mouseInputData: MouseInputData) {
    const camera = this.getTopDownCamera();
    if (!this.frameObject.visible || !camera) {
      return undefined;
    }

    const { x, y } = mouseInputData.pointer.position;
    const local = new Vector3(x, y, 0).unproject(camera);
    const origo = this.getOrigo();
    return new Vector2(local.x + origo.x, local.y + origo.y);
  }

  private getPickTolerance() {
    const camera = this.getTopDownCamera();
    const canvasHeight = cameraManager().getCanvasHeight();
    if (!camera || canvasHeight <= 0) {
      return 0;
    }

    return (PICK_TOLERANCE_PX * camera.getViewSize().y) / canvasHeight;
  }

  private getTopDownCamera() {
    const camera = cameraManager().getActiveCamera();
    return camera instanceof SkyviewOrthographicCamera ? camera : undefined;
  }

  private getOrigo() {
    return CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
  }
}
